import type { CSSProperties, RefObject } from 'react';
import '../components/atomic/layout/app_screen/app_screen.css';
import './level_screen.css';
import { containFitScale } from '../../../infrastructure/runtime';
import { useAppFit } from '../../hooks/useAppFit';
import type { CharacterId } from '../../../domain/ids/ids';
import type { LevelScreenModel } from './LevelScreenModelScreen';
import { CollectibleProgress } from './collectible_progress/CollectibleProgress';
import { HealthBar } from './HealthBar';
import { LevelName } from './level_names/LevelName';
import { PauseButton } from './pause_button/PauseButton';
import { ProgressBar } from './progress_bar/ProgressBar';
import type { LevelScreenController } from './LevelScreenController';
import type { ScreenViewProps } from '../shared/screenViewProps';
import type { LevelRunSnapshot } from '../../store/settleLevelRun';
import type { PlayfieldMotionBindings } from './gameplay/playfieldMotionBindings';
import { LevelGameplay } from './gameplay/LevelGameplay';
import { SecondChanceMinigameOverlay } from '../second_chance_minigame/SecondChanceMinigameOverlay';

export type LevelScreenViewProps = ScreenViewProps<
  LevelScreenModel,
  LevelScreenController
> & {
  characterId: CharacterId;
  isPaused: boolean;
  minigameOpen: boolean;
  secondChanceRestoreKey: number;
  motionBindingsRef: RefObject<PlayfieldMotionBindings | null>;
  onHudUpdate: (state: {
    health: number;
    maxHealth: number;
    collectiblesCollected: number;
    collectiblesTotal: number;
  }) => void;
  onRunSnapshotUpdate?: (snapshot: LevelRunSnapshot) => void;
  onSecondChanceRequest: (snapshot: LevelRunSnapshot) => void;
  onMinigameWin: () => void;
  onMinigameLose: (snapshot: LevelRunSnapshot) => void;
  pendingFailSnapshot: LevelRunSnapshot | null;
};

export function LevelScreenView({
  model,
  controller,
  characterId,
  isPaused,
  minigameOpen,
  secondChanceRestoreKey,
  motionBindingsRef,
  onHudUpdate,
  onRunSnapshotUpdate,
  onSecondChanceRequest,
  onMinigameWin,
  onMinigameLose,
  pendingFailSnapshot,
}: LevelScreenViewProps) {
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);
  const motionPaused = isPaused || minigameOpen;

  return (
    <div className="app-screen level-screen">
      <LevelGameplay
        giantId={model.giantId}
        orderIndex={model.orderIndex}
        characterId={characterId}
        isPaused={motionPaused}
        secondChanceRestoreKey={secondChanceRestoreKey}
        motionBindingsRef={motionBindingsRef}
        onHudUpdate={onHudUpdate}
        onRunSnapshotUpdate={onRunSnapshotUpdate}
        onSuccess={(snapshot) => model.onSuccess?.(snapshot)}
        onFail={(snapshot) => model.onFail?.(snapshot)}
        onSecondChanceRequest={onSecondChanceRequest}
      />
      <div
        className="level-screen-hud"
        style={
          {
            '--fit-contain-inv-x': String(contain.invX),
            '--fit-contain-inv-y': String(contain.invY),
            '--fit-contain-uniform': String(contain.uniform),
          } as CSSProperties
        }
      >
        <LevelName id={model.giantId} orderIndex={model.orderIndex} />
        <ProgressBar value={model.progress} motionBindings={motionBindingsRef} />
        <HealthBar value={model.health} max={model.maxHealth} />
        <CollectibleProgress
          collected={model.collectiblesCollected}
          total={model.collectiblesTotal}
        />
        {!minigameOpen ? <PauseButton onClick={controller.handlePause} /> : null}
      </div>
      {minigameOpen ? (
        <SecondChanceMinigameOverlay
          characterId={characterId}
          giantId={model.giantId}
          onWin={onMinigameWin}
          onLose={() => {
            onMinigameLose(
              pendingFailSnapshot ?? {
                elapsedSec: 0,
                sparksCollected: 0,
                sparksTotal: 0,
                speedBoostersCollected: 0,
                speedBoostersTotal: 0,
                collisions: 0,
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}
